import { Box, Tooltip, styled } from "@mui/material";
import React, { useEffect, useState } from "react";

import { ValidatedTextField, ValidatedTextFieldProps } from "../Helpers/ValidatedTextField";
import EditDotIcon from "./RibbonEditDotIcon";

interface RibbonValidatedFieldProps extends ValidatedTextFieldProps {
  /** Make this prop from `ValidatedTextFieldProps` required since this component must always be controlled. */
  value: string | number;
  /** The value to revert to when the toggle button is selected. */
  revertValue?: string | number;
}

/** Extension of ValidatedTextField adding support for cached/revert value swap. */
const RibbonValidatedTextField: React.FC<RibbonValidatedFieldProps> = ({
  disabled,
  fullWidth = true,
  revertValue,
  value,
  onChange,
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState<string>("");
  const [cachedValue, setCachedValue] = useState<string>();

  useEffect(() => setInternalValue(String(value)), [value]);

  const handleToggle = () => {
    if (cachedValue) {
      setInternalValue(cachedValue);
      onChange && onChange(String(cachedValue));
      setCachedValue(undefined);
    } else {
      setCachedValue(internalValue);
      setInternalValue(String(revertValue));
      onChange && onChange(String(revertValue));
    }
  };

  const onFieldChange = (value?: string) => {
    if (cachedValue !== value) setCachedValue(undefined);
    setInternalValue(value ?? "");
    onChange && onChange(value);
  };

  return (
    <Box>
      {!disabled && ((revertValue !== undefined && revertValue != internalValue) || cachedValue) && (
        <Tooltip title={cachedValue ? `Switched value: ${cachedValue}` : `Previously submitted value: ${revertValue}`}>
          <EditDotIcon color={cachedValue ? "primary" : "secondary"} onClick={handleToggle} />
        </Tooltip>
      )}
      <StyledValidatedTextField
        {...rest}
        isRevertDotShown={!disabled && revertValue !== undefined && revertValue != value}
        fullWidth={fullWidth}
        disabled={disabled}
        value={internalValue}
        onChange={onFieldChange}
      />
    </Box>
  );
};

interface StyledValidatedTextFieldProps extends ValidatedTextFieldProps {
  /** Flag indicating that the field has a revert value, and that the revert dot is shown. */
  isRevertDotShown: boolean;
}

/** A styled validated text field which outlines fields with the revert dot showing in a secondary color. */
const StyledValidatedTextField = styled(ValidatedTextField, {
  shouldForwardProp: prop => prop !== "isRevertDotShown"
})<StyledValidatedTextFieldProps>(({ error, isRevertDotShown, theme }) => ({
  "& .MuiOutlinedInput-notchedOutline":
    !error && isRevertDotShown
      ? {
          borderColor: theme.palette.secondary.main,
          borderWidth: ".15em"
        }
      : undefined
}));

export default RibbonValidatedTextField;
